import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  GroupNangoConnection,
  canConnectAgain,
  connectionLabel
} from '@/domain/nango-connection'
import { useNangoReconnect } from '@/application/manage-nango-connections'
import {
  providerName,
  RAW_TEXT,
  useOtherStoreCopy,
  useStoppedCopy,
  useUsedBySummary
} from './hub-parts'

const { Badge, Button, Card } = Base

export function NeedsAttention({
  group,
  groupStores,
  broken,
  onReconnected,
  readOnly
}: {
  group: string
  groupStores: string[]
  broken: GroupNangoConnection[]
  onReconnected: () => void
  readOnly: boolean
}) {
  const { t } = useTranslation()
  const stopped = useStoppedCopy()
  const otherStore = useOtherStoreCopy()
  const usedBy = useUsedBySummary()
  const reconnect = useNangoReconnect(group, onReconnected)
  if (broken.length === 0) return null

  return (
    <Card className="flex flex-col gap-3 p-5">
      <p className="text-sm font-medium text-neutral-900">
        {t('integrations_hub.attention.title')}
      </p>
      {broken.map((connection) => (
        <div
          key={`${connection.integration}/${connection.connectionId}`}
          className="flex items-center gap-3"
        >
          <Badge tone="danger" dot>
            {t('integrations_hub.attention.action_needed')}
          </Badge>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-sm text-neutral-900">{stopped(connection)}</p>
            {otherStore(connection) ? (
              <p className="text-xs text-neutral-600">
                {otherStore(connection)}
              </p>
            ) : null}
            <p className="text-xs text-neutral-600">
              {usedBy(connection.linkedEcommerceIds, groupStores)}
            </p>
          </div>
          {!readOnly && canConnectAgain(connection) ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-label={t('integrations_hub.attention.reconnect_aria', {
                name: providerName(connection),
                connection: connectionLabel(connection),
                ...RAW_TEXT
              })}
              onClick={() => reconnect(connection)}
            >
              {t('integrations_hub.attention.reconnect')}
            </Button>
          ) : null}
        </div>
      ))}
    </Card>
  )
}
